// src/utils/tallyBill.js
// D-291 P3d-5c - a vendor's PURCHASE BILL as a Tally purchase voucher (pure, Jest).
//
// WHY THIS FILE EXISTS
// The old Tally purchase export made one voucher per completed PO from
// po.grand_total, with no GST (POs carry no tax_percent) and no vendor
// invoice no. Since D-291 P3d the vendor's bill (purchase_bills) is the
// purchase document: taxable, CGST / SGST / IGST, charges, round off, total.
// This file turns ONE bill into the voucher's ledger lines, in paise, and
// refuses a bill whose lines do not add up to its total (listed as a
// problem - never silently skipped).
// Rules (Ravi 28 Oct):
//   - tax invoice -> exported. A PROFORMA with a vendor GSTIN is HELD until it
//     becomes the tax invoice; a proforma with NO GSTIN on the bill is the
//     vendor's final bill -> exported, no GST.
//   - GST credit (itc_eligible) -> Input CGST / SGST / IGST ledgers; no credit
//     -> the GST is part of the cost (Purchases ledger), same as Zeno's stock cost.
//   - bill charges (freight, packing ...) -> the Purchase charges ledger, or
//     Purchases when that is not set.
//   - round off -> the Round-off ledger (debit when it adds, credit when it cuts).
// Also decides which OLD POs (GRN before bills existed) still export as a PO
// voucher, so no purchase reaches Tally twice.

export const TALLY_DEFAULTS = {
  purchase:  "Purchases",
  roundOff:  "Round Off",
  inputCgst: "Input CGST",
  inputSgst: "Input SGST",
  inputIgst: "Input IGST",
};

const toP = (n) => Math.round(Number(n || 0) * 100);
const text = (v) => String(v ?? "").trim();

/** Ledger names from the company doc (Company Profile -> Tally mapping); blanks fall back to defaults. */
export function tallyLedgersOf(company = {}) {
  const c = company || {};
  const purchase = text(c.tally_purchase_ledger) || TALLY_DEFAULTS.purchase;
  return {
    purchase,
    charges:   text(c.tally_purchase_charges_ledger) || purchase,
    roundOff:  text(c.tally_round_off_ledger) || TALLY_DEFAULTS.roundOff,
    inputCgst: text(c.tally_input_cgst) || TALLY_DEFAULTS.inputCgst,
    inputSgst: text(c.tally_input_sgst) || TALLY_DEFAULTS.inputSgst,
    inputIgst: text(c.tally_input_igst) || TALLY_DEFAULTS.inputIgst,
  };
}

/** A proforma from a vendor WITH a GSTIN waits for the tax invoice. */
export const isHeldProforma = (bill = {}) =>
  bill.bill_type === "proforma" && text(bill.vendor_gstin) !== "";

/**
 * ONE bill -> the purchase voucher's ledger lines.
 * @returns {{ ok, status: "export"|"held"|"problem", reason, bill_id, vendor, ref,
 *   date ("YYYY-MM-DD" or null = use created_at), po_number, totalP,
 *   entries: [{ ledger, drP }] }}  drP > 0 debit, < 0 credit (paise);
 *   the vendor line (credit totalP) is not in entries.
 */
export function planTallyPurchase(bill = {}, ledgers = tallyLedgersOf({})) {
  const isProforma = bill.bill_type === "proforma";
  const base = {
    bill_id:   bill.id || "",
    vendor:    text(bill.vendor_name) || "Unknown Vendor",
    ref:       isProforma ? `Proforma ${text(bill.po_number)}`.trim() : text(bill.vendor_invoice_no),
    date:      !isProforma && text(bill.vendor_invoice_date) ? text(bill.vendor_invoice_date) : null,
    po_number: text(bill.po_number),
    totalP:    toP(bill.bill_total),
  };
  const problem = (reason) => ({ ...base, ok: false, status: "problem", reason, entries: [] });

  if (isHeldProforma(bill)) {
    return { ...base, ok: false, status: "held", reason: "Proforma - waiting for the vendor's tax invoice", entries: [] };
  }
  if (!(base.totalP > 0)) return problem("The bill total is 0");

  const itemsP = bill.items_taxable !== undefined && bill.items_taxable !== null
    ? toP(bill.items_taxable)
    : toP(Number(bill.taxable_total || 0) - Number(bill.charges_taxable || 0));
  const chargesP = toP(bill.charges_taxable);
  const cgstP = toP(bill.cgst);
  const sgstP = toP(bill.sgst);
  const igstP = toP(bill.igst);
  const taxP = cgstP + sgstP + igstP;
  const roundP = toP(bill.round_off);
  const credit = bill.itc_eligible === true;

  const lines = [];
  const add = (ledger, drP) => {
    if (drP === 0) return;
    const hit = lines.find((l) => l.ledger === ledger);
    if (hit) hit.drP += drP; else lines.push({ ledger, drP });
  };
  add(ledgers.purchase, itemsP + (credit ? 0 : taxP));
  add(ledgers.charges, chargesP);
  if (credit) {
    add(ledgers.inputCgst, cgstP);
    add(ledgers.inputSgst, sgstP);
    add(ledgers.inputIgst, igstP);
  }
  add(ledgers.roundOff, roundP);

  const entries = lines.filter((l) => l.drP !== 0);
  const sumP = entries.reduce((s, l) => s + l.drP, 0);
  if (sumP !== base.totalP) {
    return problem(`The bill's lines add up to ₹${(sumP / 100).toFixed(2)}, not its total ₹${(base.totalP / 100).toFixed(2)}`);
  }
  if (entries.some((l) => l.drP < 0 && l.ledger !== ledgers.roundOff)) {
    return problem("A purchase or GST line is below zero");
  }
  return { ...base, ok: true, status: "export", reason: "", entries };
}

/**
 * Old POs still exported as a PO voucher: GRN done (grn_locked) and the receipt
 * is from BEFORE purchase bills (no bill_status) - their payable is the old
 * po_grn row. A PO whose receipt has a bill_status went through the bill flow:
 * its purchase is exported from the bill (or not yet) - never as a PO.
 */
export function isLegacyPoForTally(po = {}, receiptsById = {}) {
  if (po.grn_locked !== true) return false;
  const rcId = text(po.purchase_receipt_id);
  if (!rcId) return true;                  // very old PO, no receipt id kept
  const rc = receiptsById[rcId];
  if (!rc) return false;                   // receipt not loaded -> never risk a double export
  return text(rc.bill_status) === "";
}